import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import apiService from "../../../services/api";
import { sortCampis } from "../../../utils/sortFilters";
import { filterBySearchTerm } from "../../../utils/search";
import { getInstituicaoLogo } from "../../../utils/instituicaoLogos";
import trashIcon from "../../../assets/trash.png";
import editIcon from "../../../assets/edit.png";
import { usePopup } from "../../common/Popup";

const Outer = styled.div`
  display: flex;
  justify-content: center;
  background: #f9f9f9;
  font-family: public-sans, sans-serif;
`;

const Container = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 0.6fr 0.8fr 0.5fr 160px;
  align-items: center;
  padding-left: 20px;
  height: 50px; 
  background: #f2f0f0;
  font-size: 18px;
  font-weight: 700;
  color: #6ab8a8;
  position: sticky;
  top: 0;
  gap: 0;

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    &:last-child {
      border-right: none; !important
    }
  }
`;

const HeaderAction = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
  height: 100%;
  cursor: pointer;

  img {
    width: 22px;
    height: 22px;
  }
`;

const HeaderLabel = styled.div`
  font-size: 11px;
  color: #ff9f1f;
`;

const HeaderActionsContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 1px solid #dfe3e8;
  border-bottom: 1px solid #e6e6e6;
`;

const ListRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 0.6fr 0.8fr 0.5fr 160px;
  align-items: center;
  padding-left: 20px;
  height: 50px;
  gap: 0;
  font-size: 16px;
  color: #555555;
  font-weight: 500;
  border-top: 1px solid #e6e6e6;

  &:nth-child(odd) {
    background: #e8f5f3;
  }

  &:nth-child(even) {
    background: #ffffff;
  }

  &:hover {
    background: #f9fefa;
  }

  > div {
    padding: 0 8px;
    border-right: 1px solid #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    &:last-child {
      border-right: none;
    }
  }
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  cursor: pointer;
  margin-right: 20px;
`;

const InstitutionCell = styled.div`
  display: flex;
  gap: 10px;
  margin-left: 20px; !important
  align-items: center;
  justify-content: flex-start !important;  
  text-align: left !important;     
`;

const LogoWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #6ab8a8;
  color: white;
  font-weight: bold;
  font-size: 12px;
  flex-shrink: 0;
`;

const LogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Actions = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 16px;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`;

const SmallLabel = styled.div`
  font-size: 10px;
  color: #ff9f1f;
  margin-top: 2px;
`;

const EmptyMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: #555;
  font-size: 15px;
`;

/* ---------- COMPONENT ---------- */

const CampisCadastrados = ({ onEdit, user, sortOrder, searchTerm = '' }) => {
  const popup = usePopup();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = user?.cargoId === 1 
        ? await apiService.getCampi() 
        : await apiService.getMyCampi();
      
      const items = result.data ? result.data : result;
      setItems(items);
    } catch (err) {
      setError(err.message || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchItems();
    }
  }, [user]);

  const toggleOne = (id) =>
    setSelected((s) => {
      const copy = new Set(s);
      copy.has(id) ? copy.delete(id) : copy.add(id);
      return copy;
    });

  const filteredItems = searchTerm
    ? filterBySearchTerm(items, searchTerm, [(campus) => campus.campus])
    : items;

  const sortedItems = sortOrder ? sortCampis(filteredItems, sortOrder) : filteredItems;
  const allSelected = sortedItems.length > 0 && selected.size === sortedItems.length;

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(sortedItems.map((i) => i.id)));

  const deleteOne = async (id) => {
    const confirmed = await popup.confirm({
      icon: "delete",
      message: "Confirma exclusão deste campus?",
    });
    if (!confirmed) return;
    try {
      await apiService.deleteCampus(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
      setSelected((s) => {
        const c = new Set(s);
        c.delete(id);
        return c;
      });
    } catch (err) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao deletar",
        message: err.message || "Tente novamente.",
      });
    }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    const confirmed = await popup.confirm({
      icon: "delete",
      message: "Confirma exclusão dos selecionados?",
    });
    if (!confirmed) return;
    setBulkLoading(true);
    try {
      const deletePromises = Array.from(selected).map(id => apiService.deleteCampus(id));
      await Promise.all(deletePromises);
      setItems((prev) => prev.filter((it) => !selected.has(it.id)));
      setSelected(new Set());
    } catch (err) {
      await popup.alert({
        icon: "warning",
        title: "Erro na exclusão em lote",
        message: err.message || "Tente novamente.",
      });
    } finally {
      setBulkLoading(false);
    }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("pt-BR");
    } catch {
      return iso;
    }
  };

  const renderLogo = (it) => {
    const explicitLogo = it.logoUrl;
    const mappedLogo = getInstituicaoLogo({
      naturezaJuridica: it.naturezaJuridica,
      esfera: it.esfera,
    });

    const finalLogo = explicitLogo || mappedLogo;

    if (finalLogo) {
      return (
        <LogoWrapper>
          <LogoImg src={finalLogo} alt={it.instituicao} />
        </LogoWrapper>
      );
    }

    // Fallback para iniciais caso não haja logo mapeado
    return (
      <LogoWrapper>
        {(it.instituicao || "??")
          .split(" ")
          .map((w) => w[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()}
      </LogoWrapper>
    );
  };

  const handleEdit = (campus) => {
    sessionStorage.setItem('campusEditData', JSON.stringify(campus));
    navigate('/CadastroCampis?mode=edit');
  };

  // O coordenador de curso (cargo 3) enxerga o campus em que seu curso está,
  // mas não o altera: a API responde 403 em PUT e DELETE /campis. Sem esta
  // condição a tela mostra os botões e o clique morre em erro.
  const podeAlterarCampus = user?.cargoId === 1 || user?.cargoId === 2;

  return (
    <Outer>
      <Container>
        <HeaderRow>
          <div>
            <Checkbox
              checked={allSelected}
              onChange={toggleAll}
              aria-label="Selecionar todos"
            />
          </div>
          <div>Instituição</div>
          <div>Campis</div>
          <div>Diretor</div>
          <div>Vigência convênio</div>
          
          <div>
            {podeAlterarCampus && (
              <HeaderActionsContainer>
                <HeaderAction>
                  <img src={editIcon} alt="Editar" />
                  <HeaderLabel>Editar</HeaderLabel>
                </HeaderAction>

                <HeaderAction
                  onClick={deleteSelected}
                  style={{ cursor: "pointer" }}
                >
                  <img src={trashIcon} alt="Deletar" />
                  <HeaderLabel>
                    {bulkLoading
                      ? "Deletando..."
                      : selected.size > 0
                      ? `${selected.size}`
                      : "Deletar"}
                  </HeaderLabel>
                </HeaderAction>
              </HeaderActionsContainer>
            )}
          </div>
        </HeaderRow>

        {loading && <EmptyMessage>Carregando...</EmptyMessage>}
        {error && <EmptyMessage>{error}</EmptyMessage>}
        {!loading && sortedItems.length === 0 && (
          <EmptyMessage>Nenhum campus cadastrado.</EmptyMessage>
        )}

        <ListWrapper>
          {sortedItems.map((campus) => (
            <ListRow key={campus.id}>
              <div>
                <Checkbox
                  checked={selected.has(campus.id)}
                  onChange={() => toggleOne(campus.id)}
                />
              </div>
              <InstitutionCell>
                {renderLogo(campus)}
                <span>{campus.instituicao || 'Instituição não informada'}</span>
              </InstitutionCell>
              <div>{campus.campus}</div>
              <div>{campus.diretor}</div>
              <div><b>{formatDate(campus.vigenciaTermo)}</b></div>
              <Actions>
                {podeAlterarCampus && (
                  <>
                    <IconButton
                      onClick={() => handleEdit(campus)}
                      title="Editar campus"
                    >
                      <img src={editIcon} alt="Editar" style={{ width: 22, height: 22 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => deleteOne(campus.id)}
                      title="Deletar campus"
                    >
                      <img src={trashIcon} alt="Deletar" style={{ width: 22, height: 22 }} />
                    </IconButton>
                  </>
                )}
              </Actions>
            </ListRow>
          ))}
        </ListWrapper>
      </Container>
    </Outer>
  );
};

export default CampisCadastrados;